/** Names and one-line texts for every id the menus meet, so screens hold no game text. */

import { rewardRange } from './aftermath/tally';
import {
  CATEGORIES, CATEGORY_TEXT, MISSION_TAGS, missionTags, TAG_TEXT, tagLabel,
} from './content/archetypeFacts';
import { ARCHETYPES, archetype, BIOME_NAMES, BIOMES, GROUP_1987 } from './content/archetypes';
import { HAZARDS, UNLOCKS } from './content/career';
import { CACHE_FIND, COMPANY, DIFFICULTY_NAMES, GEAR, REWARD, WEIGHT } from './content/economy';
import { INTEL, MODIFIERS } from './content/modifiers';
import { PERKS } from './content/perks';
import { MEDALS, RANK_NAMES } from './content/ranks';
import { TRAITS } from './content/traits';
import { countedName } from './gear';
import type { OperationState } from './state';
import { cacheItem, depotCandidates } from './support';
import { type RangerRecord, RANKS, type TheatreNode } from './types';

export interface Described {
  name: string;
  text: string;
}

const NODE_KINDS: Record<string, Described> = {
  mission: { name: 'Mission', text: 'One ranger, one drop. Choose who goes and what they carry.' },
  rescue: { name: 'P.O.W. Rescue', text: 'One attempt to liberate the camp. Success frees every captured ranger.' },
  rest: { name: 'Rest & Refit', text: 'All rangers -1 fatigue; the wounded advance one recovery step.' },
  supply: {
    name: 'Supply Cache',
    text: `Choose: (0) +${REWARD.supplyRp} RP, or (1) one rare item for the operation stock.`,
  },
  depot: { name: 'Replacement Depot', text: 'Recruit one ranger of three, for free.' },
  intercept: {
    name: 'Signals Intercept',
    text: "Choose: (0) reveal the next theatre's modifiers, or (1) one free intel card.",
  },
};

const STATUSES: Record<string, Described> = {
  ready: { name: 'Ready', text: 'Fit to deploy.' },
  wounded: { name: 'Seriously Wounded', text: 'Sits out until recovered.' },
  critical: { name: 'Critical', text: 'Was downed: a long recovery, and perhaps an old wound.' },
  pow: { name: 'Prisoner of War', text: 'Held by the enemy. A camp raid can bring them home.' },
  mia: { name: 'Missing in Action', text: 'Walking out. Expected back in two missions.' },
  kia: { name: 'Killed in Action', text: 'On the Memorial Wall.' },
  lost: { name: 'P.O.W. - Never Recovered', text: 'On the Memorial Wall.' },
  retired: { name: 'Retired', text: 'On the roll of honour: never lost, never used again.' },
};

function units(item: keyof typeof WEIGHT): string {
  return `${WEIGHT[item]} unit${WEIGHT[item] === 1 ? '' : 's'}`;
}

const ITEMS: Record<string, Described> = {
  magazines: { name: 'Magazine', text: '30 rounds for the carbine. 1 unit.' },
  roundsInMag: { name: 'Loaded Rounds', text: 'Rounds in the magazine on the carbine.' },
  grenades: { name: 'Hand Grenade', text: 'Troops, nests, wire, mines, light structures. 2 units.' },
  laws: { name: 'LAW', text: 'One shot. Bunkers, turrets, vehicles. 6 units.' },
  bombs: { name: 'Time Bomb', text: '5, 10 or 15 second fuse. Destroys anything destructible. 4 units.' },
  firstAid: { name: 'First-Aid Kit', text: 'Removes one wound. 2 units.' },
  smoke: {
    name: 'Smoke Grenade',
    text: `Nobody sees through the cloud, either way. Up to ${GEAR.maxSmoke}, ${units('smoke')} each. From stock.`,
  },
  nvg: { name: 'Night-Vision Goggles', text: `Night is no darker to you than dusk. ${units('nvg')}. From stock.` },
  'wire-cutters': {
    name: 'Wire Cutters',
    text: `A quiet way through barbed wire, without a scratch. ${units('wire-cutters')}. From stock.`,
  },
  'suppressed-pistol': {
    name: 'Suppressed Pistol',
    text: `${GEAR.pistolRounds} rounds, short range, barely heard. ${units('suppressed-pistol')}. From stock.`,
  },
  ghillie: {
    name: 'Ghillie Wrap',
    text: `Scrim and burlap: slower to spot while you are low. ${units('ghillie')}. From stock.`,
  },
};

/** What the two-way support nodes offer, so the dialog's buttons can say it (`choice:<kind>:<index>`). */
const CHOICES: Record<string, Described> = {
  'choice:supply:0': { name: `+${REWARD.supplyRp} RP`, text: 'Take the requisition.' },
  'choice:supply:1': {
    name: 'Rare Equipment',
    text: `One find for the operation stock: ${CACHE_FIND.smoke} smoke grenades, night-vision goggles, wire cutters `
      + 'or a suppressed pistol. It comes home with the ranger who carries it.',
  },
  'choice:intercept:0': { name: 'Theatre Report', text: 'Reveal every modifier in the next theatre.' },
  'choice:intercept:1': { name: 'Free Intel Card', text: 'One intel card, applied to the next mission flown.' },
};

function buildTable(): Map<string, Described> {
  const table = new Map<string, Described>();
  const add = (id: string, name: string, text: string): void => {
    table.set(id, { name, text });
  };
  for (const group of [NODE_KINDS, STATUSES, ITEMS, CHOICES]) {
    for (const [id, d] of Object.entries(group)) add(id, d.name, d.text);
  }
  for (const list of [PERKS, TRAITS, MEDALS, MODIFIERS, INTEL, ARCHETYPES]) {
    for (const x of list) add(x.id, x.name, x.text);
  }
  // What a mission is besides its name (10 §2): `category:<id>`, the picker's `group:<id>` and the cards' `tag:<id>`.
  for (const c of CATEGORIES) {
    add(`category:${c}`, CATEGORY_TEXT[c].name, CATEGORY_TEXT[c].text);
    add(`group:${c}`, CATEGORY_TEXT[c].name, CATEGORY_TEXT[c].text);
  }
  add('group:1987', GROUP_1987.name, GROUP_1987.text);
  for (const tag of MISSION_TAGS) add(`tag:${tag}`, TAG_TEXT[tag].name, TAG_TEXT[tag].text);
  for (const u of UNLOCKS) add(u.id, u.name, `${u.text} (${u.cost} commendations)`);
  for (const b of BIOMES) add(b, BIOME_NAMES[b], `${BIOME_NAMES[b]} theatre.`);
  RANKS.forEach((abbr, i) => {
    add(abbr, RANK_NAMES[i] ?? abbr, abbr);
    add(`rank-${i}`, RANK_NAMES[i] ?? abbr, abbr);
  });
  DIFFICULTY_NAMES.forEach((name, i) => add(`difficulty-${i}`, name, `Threat rating +${i}.`));
  for (const h of HAZARDS) add(`hazard-${h.level}`, `Hazard ${h.level}`, h.text);
  add('hq:recruit', 'Replacement', `A recruit from HQ for ${COMPANY.recruitCost} RP.`);
  add('hq:medevac', 'Priority Medevac', `One ranger advances one recovery step now, for ${COMPANY.medevacCost} RP.`);
  return table;
}

const TABLE = buildTable();

/** Table lookup; an unknown id describes itself. */
export function describeId(id: string): Described {
  return TABLE.get(id) ?? { name: id, text: '' };
}

/** A node of the running operation: what the card says, including the options of a choice node. */
export function describeNode(op: OperationState, node: TheatreNode): Described {
  const kind = describeId(node.kind);
  if (node.kind === 'depot') {
    const traits = (r: RangerRecord): string => r.traits.map((t) => describeId(t).name).join(', ');
    const options = depotCandidates(op, node)
      .map((r, i) => `(${i}) ${r.firstName} ${r.surname} "${r.callsign}" - ${traits(r)}`);
    return { name: kind.name, text: `${kind.text} ${options.join('; ')}` };
  }
  if (node.kind === 'supply') {
    // The cache's contents are fixed by the node, so the choice is made with open eyes.
    const find = countedName(cacheItem(op, node), CACHE_FIND[cacheItem(op, node)]);
    return { name: kind.name, text: `Choose: (0) +${REWARD.supplyRp} RP, or (1) ${find} for the operation stock.` };
  }
  if (!node.params) return kind;
  const p = node.params;
  const info = archetype(p.archetype);
  const tags = [
    node.keystone ? 'KEYSTONE' : '', node.variant ? 'VARIANT' : '',
    ...missionTags(info.facts).map((tag) => tagLabel(tag, info.facts)),
  ].filter((t) => t !== '').join(' ');
  const [low, high] = rewardRange(node);
  const mods = p.modifiers.map((m) => describeId(m).name).join(', ');
  return {
    name: `${node.kind === 'rescue' ? 'P.O.W. Rescue: ' : ''}${info.name}${tags ? ` [${tags}]` : ''}`,
    text: `${CATEGORY_TEXT[info.facts.category].name}. ${BIOME_NAMES[p.biome]}, ${p.timeOfDay}, ${p.weather}.`
      + ` Threat ${p.threat}. Reward ${low}-${high} RP.`
      + (mods ? ` Modifiers: ${mods}.` : '')
      + (p.dogTeams ? ` Dog teams: ${p.dogTeams}.` : '')
      + (node.tagRecovery ? ` ${node.tagRecovery.name}'s tags lie here.` : ''),
  };
}
